import styled from '@emotion/styled';
import { Checkbox, Input, message, Modal } from 'antd';
import { Button, Col, FormItem, Row, Text } from 'components';
import { theme } from 'configs/styled';
import { useFormik } from 'formik';
import {
  ILocalStorageSignup,
  useLocalStorageSignup,
} from 'hooks/useLocalStorageSignup';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { authenticationService } from 'services/driverPortal';
import {
  ILoginVerifyRequest,
  OTPResponse,
} from 'services/driverPortal/AuthenticationService.interface';
import { IResponseError } from 'utils/transformResponseError';
import * as Yup from 'yup';
import { ASCII_DIGITS_ONLY, TH_MOBILE_NO_SAME } from '../../../utils/validate';
import { phoneRegExp } from '../SignupPage';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

const validateLogin = Yup.object().shape({
  phone: Yup.string()
    .matches(phoneRegExp, 'เบอร์โทรศัพท์นี้ไม่ถูกต้อง หรือไม่มีในระบบ')
    .matches(ASCII_DIGITS_ONLY, 'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข')
    .matches(TH_MOBILE_NO_SAME, 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง')
    .required('กรุณากรอกเบอร์โทรศัพท์'),
});

const buildLoginPayload = (
  request: ILoginVerifyRequest,
  response: OTPResponse,
) => {
  return {
    ...request,
    otpRefCode: response.otpRefcode,
    expired: response.expired || '',
    otpDigit: 6,
  } as ILocalStorageSignup;
};

type LoginBlockedReason = 'criminal-not-passed';

const LoginStatusModal = styled(Modal)`
  .ant-modal-content {
    font-family: ${theme.fonts.NotoSansTh}, ${theme.fonts.NotoSands};
  }

  .ant-modal-body {
    padding: 24px;
  }
`;

const getLoginBlockedReason = (
  error: IResponseError,
): LoginBlockedReason | null => {
  const errorData = error?.data;
  const normalizedError = JSON.stringify(errorData?.error || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  if (
    normalizedError === '"criminal"' ||
    normalizedError.includes('criminal')
  ) {
    return 'criminal-not-passed';
  }

  return null;
};

const LoginForm = () => {
  const { search, pushUrl } = useQueryParams();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [isAcceptedTerm, setIsAcceptedTerm] = useState(false);
  const localStorageSignup = useLocalStorageSignup();
  const [, setShowPrompt] = usePrompt();
  const history = useHistory();
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [
    loginBlockedReason,
    setLoginBlockedReason,
  ] = useState<LoginBlockedReason | null>(null);

  useEffect(() => {
    setShowPrompt(false);

    return () => {
      message.destroy();
    };
  }, []);

  const goToOtpPage = () => {
    pushUrl('/otp', search);
  };

  const baseURLVMP = process.env.REACT_APP_VMP_URL || '';

  const handleCheckTermOfService = (e: CheckboxChangeEvent) => {
    setIsAcceptedTerm(e.target.checked);
  };

  const formik = useFormik<ILoginVerifyRequest>({
    initialValues: {
      phone: '',
    } as ILoginVerifyRequest,
    validationSchema: validateLogin,
    onSubmit: async (formValues, { setSubmitting }) => {
      if (otpLoading) return;
      setOtpLoading(true);
      try {
        const response = await authenticationService.loginVerify(formValues);

        const login = buildLoginPayload(formValues, response);
        localStorageSignup.setValues(login);

        goToOtpPage();
      } catch (error) {
        const _error = error as IResponseError;
        const blockedReason = getLoginBlockedReason(_error);

        if (blockedReason) {
          formik.setErrors({});
          setLoginBlockedReason(blockedReason);
          return;
        }

        const errorMessage =
          _error?.data?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

        let messageString = '';
        if (typeof errorMessage === 'string') {
          messageString =
            errorMessage === 'No rider found with this phone number'
              ? 'ท่านยังไม่ได้เป็นสมาชิก กรุณาสมัครสมาชิก'
              : errorMessage;
        } else {
          messageString =
            errorMessage?.message === 'No rider found with this phone number'
              ? 'ท่านยังไม่ได้เป็นสมาชิก กรุณาสมัครสมาชิก'
              : errorMessage?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
        }

        formik.setErrors({ phone: messageString });
      } finally {
        setSubmitting(false);
        setOtpLoading(false);
      }
    },
  });

  const errors = formik.errors || {};

  const handlePhone = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    formik.setFieldValue('phone', digits);
    setDisableSubmit(digits.length !== 10);
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <FormItem
          label="เบอร์โทรศัพท์"
          labelCol={{ span: 24 }}
          colon={false}
          validateStatus={errors.phone ? 'error' : ''}
          help={errors.phone}
        >
          <Input
            placeholder="ตัวอย่าง 0800000000"
            name="phone"
            size="large"
            maxLength={10}
            onChange={handlePhone}
            onBlur={formik.handleBlur}
            value={formik.values.phone ?? ''}
            inputMode="tel"
            pattern="[0-9]*"
            disabled={otpLoading}
          />
        </FormItem>
        <Row gutter={[24, 12]}>
          <Col span={1}>
            <Checkbox onChange={handleCheckTermOfService} />
          </Col>
          <Col span={22}>
            <span
              style={{
                fontSize: theme.fontSize.xxs,
              }}
            >
              ข้าพเจ้าได้อ่านและยอมรับ
              <a
                href={`${baseURLVMP}/term/T_C_2W_All_Now_Logistics.pdf`}
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                ข้อกำหนดและเงื่อนไขการเป็นผู้ให้บริการขนส่งรถจักรยานยนต์กับ
                บริษัท ออลล์ นาว โลจิสติกส์
              </a>
              &nbsp;และกับ&nbsp;
              <a
                href={`${baseURLVMP}/term/T_C_2W_All_Now_Management.pdf`}
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                บริษัท ออลล์ นาว แมนเนจเม้นท์
              </a>
              &nbsp;รวมถึง&nbsp;
              <a
                href="https://www.allnowgroup.com/policy"
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                ประกาศเกี่ยวกับการคุ้มครองข้อมูลส่วนบุคคล
              </a>
              {/*&nbsp;และ*/}
              {/*<Link*/}
              {/*  to={'/driver/terms-condition'}*/}
              {/*  target="_blank"*/}
              {/*  style={{ marginLeft: '3px' }}*/}
              {/*>*/}
              {/*  ข้อตกลงและเงื่อนไขการใช้งานเว็บไซต์*/}
              {/*</Link>*/}
            </span>
          </Col>
        </Row>
        <FormItem>
          <Button
            style={{ marginTop: theme.spacing.xxl }}
            block
            size="large"
            htmlType="submit"
            disabled={disableSubmit || !isAcceptedTerm}
            loading={otpLoading}
            type={disableSubmit || !isAcceptedTerm ? 'default' : 'primary'}
          >
            ยืนยันและส่งรหัส OTP
          </Button>
        </FormItem>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '10px',
          }}
        >
          <Text>ยังไม่เคยสมัคร?</Text>
          <Link
            style={{ paddingLeft: '5px' }}
            to={`/signup${history?.location?.search}`}
          >
            คลิ๊กเพื่อสมัคร
          </Link>
        </div>
      </form>
      <LoginStatusModal
        visible={loginBlockedReason !== null}
        closable={false}
        footer={null}
        centered
        width={480}
        onCancel={() => setLoginBlockedReason(null)}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#4A4A4A',
            marginBottom: 10,
            textAlign: 'center',
          }}
        >
          ผลการสมัครเป็นคนขับกับ ALL NOW
        </div>

        <div
          style={{
            backgroundColor: '#FFF1F1',
            border: '1px solid #FFD6D6',
            padding: '20px 16px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: '#E00000',
            }}
          >
            ไม่ผ่าน
          </div>

          <div style={{ fontSize: 12, color: '#4A4A4A', lineHeight: 1.6 }}>
            ผลการสมัครของท่านไม่ผ่านการตรวจประวัติอาชญากรรมตามเกณฑ์ของบริษัท
          </div>
        </div>

        <Button
          block
          size="large"
          type="primary"
          style={{ marginTop: theme.spacing.xxl }}
          onClick={() => setLoginBlockedReason(null)}
        >
          ตกลง
        </Button>
      </LoginStatusModal>
    </>
  );
};

export default LoginForm;
